import React, { useState, useCallback } from 'react'
import copy from 'copy-to-clipboard'

import { dataIdFromObject } from '@sketch/graphql-cache'
import { useDebounceValue } from '@sketch/utils'

import {
  Link,
  Button,
  Flex,
  Input,
  Box,
  useModalContext,
  ConfirmationDialog,
  LoadingState,
  ErrorMessage,
  InfiniteList,
  handleFetchMore,
} from '@sketch/components'
import {
  Section,
  Title,
  Description,
} from '../WorkspaceSettingsProfile/WorkspaceSettingsProfile.styles'
import {
  DocumentPreviewsLayout,
  PreviewRowDocument,
  PreviewRow,
} from './DocumentPreviewsLayout'

import { routes } from '@sketch/modules-common'

import {
  useDeleteAllPreviewsMutation,
  useGetWorkspaceSharesWithPreviewsQuery,
  useRefreshAllPreviewsMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { PartialShareModalEdit } from '../../../shares/modals/PartialShareModal'
import { EmptyState } from '../../../shares/components/EmptyState'

export interface WorkspaceSettingsDocumentPreviewsProps {
  workspaceIdentifier: string
}

export const WorkspaceSettingsDocumentPreviews: React.FCC<
  WorkspaceSettingsDocumentPreviewsProps
> = ({ workspaceIdentifier }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300)
  const isSearching = !!debouncedSearchTerm.length

  const { showModal, hideModal } = useModalContext()
  const { showToast } = useToast()

  // Fetch shares that have previews using HAS_PREVIEW filter
  const {
    data,
    fetchMore,
    refetch,
    loading: sharesLoading,
    error: sharesError,
  } = useGetWorkspaceSharesWithPreviewsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      workspaceIdentifier,
      search: { filters: ['HAS_PREVIEWS'], name: debouncedSearchTerm },
    },
  })

  const after = data?.workspace.shares.meta?.after ?? null

  const [refreshAllPreviews, { loading: refreshAllLoading }] =
    useRefreshAllPreviewsMutation({
      variables: { workspaceIdentifier },
      onError: 'show-toast',
      onCompleted: () => {
        showToast('All preview links refreshed')
        refetch()
        hideModal()
      },
    })

  const [deleteAllPreviewsMutation, { loading: deleteAllLoading }] =
    useDeleteAllPreviewsMutation({
      variables: { workspaceIdentifier },
      onError: 'show-toast',
      onCompleted: () => {
        showToast('All document previews removed')
        refetch()
        hideModal()
      },
    })

  const handleRefreshAll = useCallback(() => {
    showModal(ConfirmationDialog, {
      title: 'Refresh All Preview Links?',
      children: (
        <p>
          Existing preview links will no longer work and you&apos;ll need to
          share new links with anyone who needs them.
        </p>
      ),
      confirmButton: {
        text: 'Refresh',
        variant: 'primary',
        loading: refreshAllLoading,
      },
      cancelButton: {
        text: 'Cancel',
      },
      onConfirm: () => {
        refreshAllPreviews()
      },
      onHide: hideModal,
    })
  }, [showModal, refreshAllLoading, refreshAllPreviews, hideModal])

  const handleRemoveAll = useCallback(() => {
    showModal(ConfirmationDialog, {
      title: 'Remove All Document Previews?',
      children: (
        <p>
          Any existing preview links will no longer work and no one will be able
          to access them.
        </p>
      ),
      confirmButton: {
        text: 'Remove All',
        loading: deleteAllLoading,
      },
      cancelButton: {
        text: 'Cancel',
      },
      onConfirm: () => {
        deleteAllPreviewsMutation()
      },
      onHide: hideModal,
    })
  }, [showModal, deleteAllLoading, deleteAllPreviewsMutation, hideModal])

  // Re-show modals when loading states change to update button states
  React.useEffect(() => {
    if (refreshAllLoading) {
      handleRefreshAll()
    }
  }, [refreshAllLoading, handleRefreshAll])

  React.useEffect(() => {
    if (deleteAllLoading) {
      handleRemoveAll()
    }
  }, [deleteAllLoading, handleRemoveAll])

  const handleCopyLink = (previewIdentifier: string) => {
    const previewPath = routes.PREVIEW_VIEW.create({
      previewID: previewIdentifier,
    })
    const fullUrl = `${window.location.origin}${previewPath}`
    copy(fullUrl)
    showToast('Preview link copied')
  }

  const handlePreviewSettings = (
    documentIdentifier: string,
    previewIdentifier: string
  ) => {
    const share = data?.workspace.shares.entries.find(
      share => share.identifier === documentIdentifier
    )
    if (!share) return

    const images = share.version?.document?.previewFile?.thumbnails ?? []
    const shareThumbnail = images[images.length - 1]?.url ?? undefined

    showModal(PartialShareModalEdit, {
      shareIdentifier: share.identifier,
      shareName: share.name,
      shareThumbnail,
      versionShortId: share.version?.identifier ?? '',
      existingPreviewId: previewIdentifier,
    })
  }

  const handleOpenPreview = (previewIdentifier: string) => {
    window.open(
      routes.PREVIEW_VIEW.create({ previewID: previewIdentifier }),
      '_blank'
    )
  }

  const items: PreviewRowDocument[] =
    data?.workspace.shares.entries.map(share => {
      const images = share.version?.document?.previewFile?.thumbnails ?? []
      const smallestThumbnail = images[images.length - 1]

      return {
        identifier: share.identifier,
        name: share.name,
        thumbnailUrl: smallestThumbnail?.url ?? undefined,
        previews: share.previews.filter(p => !!p),
      }
    }) ?? []

  const disableControls = !items.length && !isSearching

  const emptyStateIcon = isSearching ? 'search' : 'preview'
  const emptyStateDescription = isSearching
    ? 'There are no documents with previews matching your search.'
    : 'There are no document previews in this Workspace.'

  return (
    <>
      <Section>
        <Title bold>Document Previews</Title>
        <Description>
          With Previews, you can control which pages and prototypes of a Sketch
          Document to share, and who you share them with.{' '}
          {/* @TODO: add link for learn more */}
          <Link external href="https://sketch.com">
            Learn More
          </Link>
        </Description>
        <Flex mt={4}>
          <Button
            onClick={handleRefreshAll}
            variant="secondary"
            disabled={disableControls}
          >
            Refresh all links
          </Button>
          <Button
            onClick={handleRemoveAll}
            variant="negative-secondary"
            disabled={disableControls}
          >
            Remove all previews
          </Button>
        </Flex>
      </Section>

      <Section secondary>
        <Title>Document Previews</Title>

        {!disableControls && (
          <Box mt={4} mb={6}>
            <Input
              placeholder="Search documents..."
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </Box>
        )}

        {sharesLoading ? (
          <LoadingState />
        ) : sharesError ? (
          <ErrorMessage.Generic />
        ) : !items.length ? (
          <EmptyState
            title="No document previews found"
            description={emptyStateDescription}
            icon={emptyStateIcon}
          />
        ) : (
          <InfiniteList
            canLoadMore={after !== null}
            onLoadMore={handleFetchMore(
              fetchMore,
              ['workspace', 'shares', 'entries'],
              { dataIdFromObject, after }
            )}
          >
            <DocumentPreviewsLayout
              header={[{ label: 'Document' }, { label: 'Preview' }]}
              items={items}
              renderItem={(document: PreviewRowDocument) => (
                <PreviewRow
                  document={document}
                  onCopyLink={handleCopyLink}
                  onPreviewSettings={handlePreviewSettings}
                  onOpenPreview={handleOpenPreview}
                />
              )}
            />
          </InfiniteList>
        )}
      </Section>
    </>
  )
}
